import { useCallback, useContext, useEffect, useState } from "react";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { MOBILE_DOMAIN } from "@constants";
import { ESubscriptionStatus, ICustomService, IServicePage, ISubscriptionResponse } from "../interface/ISubscription";
import { GET_SUBSCRIPTION_LIST_ROUTE, getSearchServiceListRoute } from "@api-routes/accounts";
import { useGetAccountDetails } from "@features/AccountDetails/hook/useGetAccountDetails";
import { mapSearchServiceListQuery } from "@features/AccountDetails/mappers/mapSearchServiceListQuery";
import { ISelectOption } from "itsf-ui-common";
import { useFetch } from "@common/hook/useFetch";

type CustomerServiceOption = ISelectOption<ICustomService>;

export const useGetSubscription = (hasDefault: boolean) => {
    const { selectedService, setSelectedService } = useContext(SelectedServiceContext);
    const [servicesList, setServicesList] = useState<CustomerServiceOption[]>([]);
    const [getSubscriptionListState, { get: getSubscriptions }] = useFetch();
    const [getServiceListState, { get: getServices }] = useFetch();
    const { accountDetails } = useGetAccountDetails();

    const getPhoneNumber = (phoneNumbersOptions: CustomerServiceOption[]) => (
        prevSelectedService: ICustomService | undefined
    ) => {
        const phoneNumber = phoneNumbersOptions.length ? phoneNumbersOptions[0].value : undefined;
        const noDefaultValue = phoneNumbersOptions.length > 1 ? undefined : phoneNumber;
        const defaultValue = prevSelectedService ?? phoneNumber;

        return hasDefault ? defaultValue : noDefaultValue;
    };

    const getSubscriptionList = useCallback(() => {
        if (!accountDetails?.id) {
            return;
        }
        const promiseSubscriptionList = getSubscriptions<ISubscriptionResponse[]>(GET_SUBSCRIPTION_LIST_ROUTE);
        const promiseServiceList = getServices<IServicePage>(
            getSearchServiceListRoute(mapSearchServiceListQuery(accountDetails?.id))
        );

        Promise.all([promiseSubscriptionList, promiseServiceList]).then(
            ([subscriptionListResponse, serviceListResponse]) => {
                const phoneNumbersOptions: CustomerServiceOption[] = [];
                if (subscriptionListResponse?.length && serviceListResponse?.content.length) {
                    const activeSubscriptionList = subscriptionListResponse?.filter(
                        (sub) => sub.status === ESubscriptionStatus.ACTIVE
                    );
                    serviceListResponse.content
                        .filter((service) => service.domain === MOBILE_DOMAIN)
                        .forEach((service) => {
                            const subscription = activeSubscriptionList.find(
                                (sub) => sub.id === service.subscriptionId
                            );
                            if (subscription) {
                                phoneNumbersOptions.push({
                                    label: service.name,
                                    value: {
                                        catalogOfferCode: subscription.catalogOfferCode,
                                        id: service.id,
                                        name: service.name,
                                        status: subscription.status,
                                        subscriptionId: service.subscriptionId,
                                    },
                                });
                            }
                        });

                    setSelectedService(getPhoneNumber(phoneNumbersOptions));
                }
                setServicesList(phoneNumbersOptions);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountDetails?.id]);

    useEffect(getSubscriptionList, [getSubscriptionList]);

    const handleChangeSelectService = (selectedPhoneNumber: number) => {
        const serviceSelected = servicesList.find((service) => service.value.id === selectedPhoneNumber);
        if (serviceSelected) {
            setSelectedService(serviceSelected.value);
        }
    };

    return {
        getSubscriptionList,
        getSubscriptionListState,
        getServiceListState,
        handleChangeSelectService,
        selectedService,
        servicesList,
    };
};
